<template>
  <div class="map-wrap">
    <div ref="map" class="map"></div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";
import iconUrl from '@/assets/greenspot.png';
import iconActiveUrl from '@/assets/greenspot_audio.png';

const redSpotIcon = L.icon({
  iconUrl,
  iconSize: [22, 22],      // an dein PNG anpassen
  iconAnchor: [8, 16],    // Mittelpunkt unten
});

const activeSpotIcon = L.icon({
  iconUrl: iconActiveUrl,
  iconSize: [32, 32],
  iconAnchor: [11, 22],
});

export default {
  name: "RouteMap",
  props: {
    points: {
      type: Array,
      required: true, // [[lat,lng], [lat,lng], ...]
    },
    zoom: { type: Number, default: 15 },
    dialogOpen: { type: Boolean, default: false },
    tourdata: {
      type: Object,
      required: false,
    },
    orsApiKey: { type: String, required: true },
    orsProfile: { type: String, default: "foot-walking" }
  },
  data() {
    return {
      map: null,
      routing: null,
      markers: [],          // <- Marker-Instanzen
      activeIndex: 0       // <- welcher Punkt ist aktiv?
    };
  },
  watch: {    
    activePointIndex(i) {
      if (!this.map) return;
      this.activeIndex = i;

      // Marker ggf. schon neu?
      this.$nextTick(() => {
        this.setActivePoint(i);
        // optional: zentrieren: 
        // const ll = this.markers[i]?.getLatLng();
        // if (ll) this.map.panTo(ll);
      });
    },
    points: {
      deep: true,
      handler() {
        if (!this.map) return;

        // Marker neu rendern + active setzen
        this.renderMarkers();
        this.setActivePoint(Number.isFinite(this.activeIndex) ? this.activeIndex : 0);

        // Routing aktualisieren
        this.updateRouting();

        // im Dialog: nach DOM-Update + Layout
        this.$nextTick(() => {
          setTimeout(() => {
            this.map.invalidateSize(true);
            if (this.points && this.points.length > 1) {
              this.map.fitBounds(L.latLngBounds(this.points), { padding: [20, 20] });
            } else if (this.points?.length === 1) {
              this.map.setView(this.points[0], this.zoom);
            }
          }, 50);
        });
      }
    }, 
    dialogOpen(val) {
      if (!val || !this.map) return;

      // Beim Öffnen: Leaflet braucht oft ein invalidateSize,
      // und du willst den aktiven Marker erneut "markieren"
      this.$nextTick(() => {
        setTimeout(() => {
          this.map.invalidateSize(true);

          // falls Punkte inzwischen gewechselt haben:
          this.renderMarkers();
          // this.setActivePoint(this.activeIndex || 0);
          this.setActivePoint(Number.isFinite(this.activeIndex) ? this.activeIndex : 0);
          this.updateRouting();

          if (this.points && this.points.length > 1) {
            this.map.fitBounds(L.latLngBounds(this.points), { padding: [20, 20] });
          } else if (this.points?.length === 1) {
            this.map.setView(this.points[0], this.zoom);
          }
        }, 300); // bei Dialog-Animation lieber etwas höher
      });
    },
    // Alter Code:   
    // dialogOpen(val) {
    //   console.log('Watch map when open');
    //   if (val && this.map) {
    //     this.$nextTick(() => {
    //       setTimeout(() => {
    //         console.log('nach timeout');
    //         this.map.invalidateSize(true);

    //         // optional: neu fitten
    //         this.map.fitBounds(
    //           L.latLngBounds(this.points),
    //           { padding: [20, 20] }
    //         );
    //       }, 400);
    //     });
    //   }
    // }
  },
  mounted() {    
    // 1) Karte
    this.map = L.map(this.$refs.map).setView(this.points[0], this.zoom);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap",
    }).addTo(this.map);

    // 2) Marker
    // this.points.forEach((p, i) => {
    //   L.marker(p,{ icon: redSpotIcon }).addTo(this.map).bindPopup(`Punkt ${i + 1}`);
    // });

    this.renderMarkers();     // <- NEU
    this.setActivePoint(0);   // <- NEU: initialer aktiver Punkt

    // 3) Routing (ORS + Leaflet Routing Machine)
    this.updateRouting();

    // 4) Initial resize/fit (z.B. im Dialog)
    setTimeout(() => {
      if (!this.map) return;
      this.map.invalidateSize(true);
      if (this.points && this.points.length > 1) {
        this.map.fitBounds(L.latLngBounds(this.points), { padding: [20, 20] });
      }
    }, 200);
  },
  beforeDestroy() { // Vue 2
    if (this.map) this.map.remove();
  },
  methods: {

    updateRouting() {
      if (!this.orsApiKey) return;
      if (!this.map) return;
      if (!this.points || this.points.length < 2) return;

      const waypoints = this.points.map((p) => L.latLng(p[0], p[1]));

      // Falls schon existiert: nur Waypoints aktualisieren
      if (this.routing) {
        this.routing.setWaypoints(waypoints);
        return;
      }

      // ORS Router für Leaflet Routing Machine
      const orsRouter = this.makeOrsRouter({
        apiKey: this.orsApiKey,
        profile: this.orsProfile,
      });

      this.routing = L.Routing.control({
        waypoints,
        router: orsRouter,
        createMarker: (i, wp) => L.marker(wp.latLng, { icon: redSpotIcon }),

        addWaypoints: false,
        draggableWaypoints: false,
        fitSelectedRoutes: true,
        routeWhileDragging: false,

        show: false,

        // NEU:
        showAlternatives: false,
        collapsible: true,
      }).addTo(this.map);

    },
    makeOrsRouter({ apiKey, profile }) {
      const endpoint = `https://api.openrouteservice.org/v2/directions/${encodeURIComponent(profile)}/geojson`;

      const OrsRouter = L.Class.extend({
        options: {
          apiKey: null,
          endpoint: null,
        },

        initialize(options) {
          // options kann mal undefined sein -> absichern
          L.setOptions(this, options || {});
          this._pendingRequest = null;
        },

        route(waypoints, callback, context) {
          if (this._pendingRequest) {
            try { this._pendingRequest.abort(); } catch (e) {
              console.warn("Fehler beim Abbrechen der vorherigen ORS-Anfrage:", e);
            }
            this._pendingRequest = null;
          }

          const controller = new AbortController();
          this._pendingRequest = controller;

          const coordinates = waypoints.map(w => [w.latLng.lng, w.latLng.lat]);

          fetch(this.options.endpoint, {
            method: "POST",
            signal: controller.signal,
            headers: {
              Authorization: this.options.apiKey,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ coordinates, instructions: false, elevation: false }),
          })
            .then(async (res) => {
              if (!res.ok) {
                const txt = await res.text().catch(() => "");
                throw new Error(`ORS ${res.status}: ${txt || res.statusText}`);
              }
              return res.json();
            })
            .then((geojson) => {
              const feat = geojson?.features?.[0];
              if (!feat?.geometry?.coordinates?.length) {
                throw new Error("ORS: Keine Route in der Antwort gefunden.");
              }

              const latLngs = feat.geometry.coordinates.map(([lng, lat]) => L.latLng(lat, lng));

              const route = {
                  name: "ORS route",
                  coordinates: latLngs,

                  // Leaflet Routing Machine erwartet das zwingend
                  instructions: [],

                  // hilfreich für internes Routing
                  inputWaypoints: waypoints,
                  waypoints: waypoints,

                  summary: feat.properties?.summary ? {
                    totalDistance: feat.properties.summary.distance,
                    totalTime: feat.properties.summary.duration,
                  } : {
                    totalDistance: 0,
                    totalTime: 0,
                  },
                };

                callback.call(context || this, null, [route]);

            })
            .catch((err) => {
              if (err?.name === "AbortError") return;
              callback.call(context || this, err);
            });
        },
      });

      // WICHTIG: mit new instanziieren
      return new OrsRouter({ apiKey, endpoint });
    },
    renderMarkers() {
      // alte Marker entfernen
      this.markers.forEach(m => m.remove());
      this.markers = [];

      this.points.forEach((p, i) => {
        const marker = L.marker(p, { icon: redSpotIcon })
          .addTo(this.map)
          // .bindPopup(`Punkt ${i + 1}`)
          .on("click", () => this.setActivePoint(i)); // <- klick macht aktiv

        this.markers.push(marker);
      });
    },

    setActivePoint(i) {
      // Alter Code:
      // if (i < 0 || i >= this.markers.length) return;

      if (!this.markers.length) return;
      if (i < 0) i = 0;
      if (i >= this.markers.length) i = this.markers.length - 1;

      // vorherigen zurücksetzen
      if (this.markers[this.activeIndex]) {
        this.markers[this.activeIndex].setIcon(redSpotIcon);
      }

      // Alter Code: alten zurücksetzen
      // if (this.activeIndex !== null && this.markers[this.activeIndex]) {
      //   this.markers[this.activeIndex].setIcon(redSpotIcon);
      // }

      // neuen aktiv setzen
      this.activeIndex = i;
      this.markers[i].setIcon(activeSpotIcon);

      // optional: Popup öffnen + zentrieren
      // this.markers[i].openPopup();
      // this.map.panTo(this.markers[i].getLatLng(), { animate: true });

      // optional: Routing auf diesen aktiven Punkt "fokussieren" (nicht nötig)
    }

  },
  unmounted() { // Vue 3 (falls du Options API in Vue 3 nutzt)
    if (this.map) this.map.remove();
  },
  computed: {
    activePointIndex() {
      // Beispiel: tourdata.activePointIndex oder fallback 0
      const i = Number(this.tourdata?.activePointIndex ?? 0);
      return Number.isFinite(i) ? i : 0;
    }
  }  
};
</script>

<style scoped>
.map-wrap { width: 100%; height: 140px; }
.map { height: 140px; width: 100%; }
</style>